import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agentProposalsService,
  type AgentProposal,
  type ProposalListParams,
} from '../services/agent-proposals.service';

const KEY = 'agent-proposals';

export type Decision = 'approve' | 'reject';

/** Selo da barra lateral. Só consulta para ADMIN. */
export function usePendingProposalsCount(enabled: boolean): number {
  const query = useQuery({
    queryKey: [KEY, 'pending-count'],
    queryFn: () => agentProposalsService.list({ status: 'PENDING', limit: 1 }),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  return query.data?.total ?? 0;
}

export function useAgentProposals(params: ProposalListParams) {
  return useQuery({
    queryKey: [KEY, 'list', params],
    queryFn: () => agentProposalsService.list(params),
  });
}

export function useAgentProposal(id: number) {
  return useQuery({
    queryKey: [KEY, 'detail', id],
    queryFn: () => agentProposalsService.get(id),
    enabled: Number.isInteger(id) && id > 0,
  });
}

export function useDecideProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      decision: Decision;
      note?: string;
      excludedItems?: string[];
    }) =>
      vars.decision === 'approve'
        ? agentProposalsService.approve(vars.id, vars.note, vars.excludedItems)
        : agentProposalsService.reject(vars.id, vars.note),
    onSuccess: (item: AgentProposal) => {
      queryClient.setQueryData([KEY, 'detail', item.id], item);
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
